// A compact, sticky section bar for long pages made of sections (the Tech Pack): chips on wide screens (wrapping to
// two lines when needed), a "Jump to section" dropdown on phones. Clicking scrolls smoothly to the section, below the
// sticky header, opens it if it was collapsed and moves keyboard focus to its heading. The section in view is
// highlighted (scroll-spy), the URL hash names it (#bom), and a link or refresh with a hash opens at that section.
// A dot on a chip flags missing required data or unsaved changes.
import { esc } from "./lib.js";

export interface BarSection { id: string; label: string; chip?: string }

export function sectionBarHtml(sections: BarSection[], label = "Sections"): string {
  return `<nav class="secbar" aria-label="${esc(label)}">
<div class="secbar-chips">${sections.map((s, i) => `<a href="#${esc(s.id)}" class="secbar-chip" data-secbar="${esc(s.id)}"><span class="secbar-n">${i + 1}</span>${esc(s.chip || s.label)}<span class="secbar-dot" hidden></span></a>`).join("")}</div>
<label class="secbar-jump"><span>Jump to section</span><select data-secbar-select>${sections.map((s, i) => `<option value="${esc(s.id)}">${i + 1}. ${esc(s.label)}</option>`).join("")}</select></label>
</nav>`;
}

export interface SectionBar { go(id: string, smooth?: boolean): void; setDot(id: string, on: boolean, title?: string): void }

/** Wire up the bar. sticky: the element that stays pinned (header + bar); target(id): the section element. */
export function mountSectionBar(opts: { sticky: HTMLElement; bar: HTMLElement; sections: BarSection[]; target: (id: string) => HTMLElement | null; jumpToHash?: boolean }): SectionBar {
  const { sticky, bar, sections } = opts;
  const ids = sections.map((s) => s.id);
  const select = bar.querySelector<HTMLSelectElement>("[data-secbar-select]")!;
  const chips = new Map([...bar.querySelectorAll<HTMLAnchorElement>("[data-secbar]")].map((a) => [a.dataset.secbar!, a]));

  // Pin below whatever else is sticky at the top of the page (the phone header).
  const topbarHeight = () => {
    const t = document.querySelector<HTMLElement>(".topbar");
    return t && getComputedStyle(t).position === "sticky" ? t.getBoundingClientRect().height : 0;
  };
  const place = () => { sticky.style.top = `${Math.round(topbarHeight())}px`; };
  const offset = () => topbarHeight() + sticky.getBoundingClientRect().height + 8;

  let active = "";
  const setActive = (id: string) => {
    if (id === active) return;
    active = id;
    for (const [k, a] of chips) { a.classList.toggle("is-active", k === id); if (k === id) a.setAttribute("aria-current", "location"); else a.removeAttribute("aria-current"); }
    select.value = id;
    // Keep the active chip visible when the chips scroll sideways.
    chips.get(id)?.scrollIntoView({ block: "nearest", inline: "nearest" });
  };

  const go = (id: string, smooth = true) => {
    const el = opts.target(id);
    if (!el) return;
    // A collapsed section opens first.
    const closed = el.matches("details:not([open])") ? el : el.querySelector<HTMLDetailsElement>(":scope > details:not([open])") || el.closest<HTMLDetailsElement>("details:not([open])");
    if (closed) (closed as HTMLDetailsElement).open = true;
    el.classList.remove("is-collapsed");
    const top = el.getBoundingClientRect().top + window.scrollY - offset();
    window.scrollTo({ top: Math.max(0, top), behavior: smooth && !matchMedia("(prefers-reduced-motion: reduce)").matches ? "smooth" : "auto" });
    const h = el.querySelector<HTMLElement>("h2, h3, h4");
    if (h) { if (!h.hasAttribute("tabindex")) h.setAttribute("tabindex", "-1"); h.focus({ preventScroll: true }); }
    // The hash names the section without adding a history entry, so Back still leaves the page.
    history.replaceState(history.state, "", `${location.pathname}${location.search}#${id}`);
    setActive(id);
    lock = Date.now() + (smooth ? 900 : 50);
  };

  let lock = 0, raf = 0;
  const spy = () => {
    raf = 0;
    if (!bar.isConnected) { cleanup(); return; }
    if (Date.now() < lock) return;
    const line = offset() + 4;
    let current = ids[0];
    for (const id of ids) {
      const el = opts.target(id);
      if (el && el.getBoundingClientRect().top <= line) current = id;
    }
    // At the very bottom, the last section counts as in view.
    if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) current = ids[ids.length - 1];
    setActive(current);
  };
  const onScroll = () => { if (!raf) raf = requestAnimationFrame(spy); };
  const onResize = () => { place(); onScroll(); };
  const onHash = () => { const id = location.hash.slice(1); if (ids.includes(id)) go(id); };
  const cleanup = () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onResize); window.removeEventListener("falcon:hash", onHash); };
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onResize);
  window.addEventListener("falcon:hash", onHash);

  bar.addEventListener("click", (e) => {
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>("[data-secbar]");
    if (!a || e.ctrlKey || e.metaKey || e.shiftKey) return;
    e.preventDefault();
    go(a.dataset.secbar!);
  });
  select.addEventListener("change", () => go(select.value));

  place();
  const start = location.hash.slice(1);
  if (opts.jumpToHash !== false && ids.includes(start)) requestAnimationFrame(() => go(start, false));
  else { setActive(ids[0]); spy(); }

  return {
    go,
    setDot(id, on, title = "") {
      const dot = chips.get(id)?.querySelector<HTMLElement>(".secbar-dot");
      if (!dot) return;
      dot.hidden = !on;
      dot.title = title;
      chips.get(id)!.title = on ? title : "";
      const opt = [...select.options].find((o) => o.value === id);
      if (opt) { const base = opt.textContent!.replace(/ •$/, ""); opt.textContent = on ? `${base} •` : base; }
    },
  };
}
